import axios from "axios";
import { serverUrl } from "../../ApiRoute";
import { jflApi } from "../common/authApi";

// The energiSync analysis service. serverUrl.aws already carries the /default
// stage. Every route here answers { success, data }; a false success or a
// thrown request is read as "no data", never as a failed report - one bad
// device or day must not sink the rest.

const RETRY_DELAY_MS = 5000;
// The spec retries throttled calls indefinitely; a cap keeps a table that stays
// throttled from hanging a report forever. At 5s apart this is ~1 minute.
const MAX_RETRIES = 12;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// DynamoDB throttling, surfaced by the service as a 400 with the exception
// name leading the message.
const isThrottled = (error) =>
  error?.response?.status === 400 &&
  String(error?.response?.data?.data?.error ?? "").startsWith(
    "ProvisionedThroughputExceededException"
  );

const postWithRetry = async (path, body, attempt = 0) => {
  try {
    return await axios.post(`${serverUrl.aws}${path}`, body);
  } catch (error) {
    if (isThrottled(error) && attempt < MAX_RETRIES) {
      await wait(RETRY_DELAY_MS);
      return postWithRetry(path, body, attempt + 1);
    }
    throw error;
  }
};

// POST /units/day -> data.snapshot.units, or {} on anything else. assetId is
// the appliance's assetId.
export const fetchUnitsDay = async (deviceId, day, assetId) => {
  try {
    const res = await axios.post(`${serverUrl.aws}/units/day`, { deviceId, assetId, day });
    return res.data?.success ? res.data?.data?.snapshot?.units ?? {} : {};
  } catch (error) {
    return {};
  }
};

// POST /analysis/day -> data.snapshot, or {}.
// assetId is the appliance's assetId.
export const fetchAnalysisDay = async (deviceId, day, assetId) => {
  try {
    const res = await postWithRetry("/analysis/day", { deviceId, assetId, day });
    return res.data?.success ? res.data?.data?.snapshot ?? {} : {};
  } catch (error) {
    return {};
  }
};

// POST /analysis/today -> data.snapshot, or {}. remake forces the service to
// rebuild today's snapshot rather than serve a stale one.
export const fetchAnalysisToday = async (deviceId, assetId) => {
  try {
    const res = await postWithRetry("/analysis/today", { deviceId, assetId, remake: true });
    return res.data?.success ? res.data?.data?.snapshot ?? {} : {};
  } catch (error) {
    return {};
  }
};

// POST /analysis/hourly/multiple -> data.hourlyData, or [].
export const fetchHourlyMultiple = async (deviceIds, startDate, endDate) => {
  try {
    const res = await axios.post(`${serverUrl.aws}/analysis/hourly/multiple`, {
      devices: deviceIds,
      startDate,
      endDate,
    });
    return res.data?.success ? res.data?.data?.hourlyData ?? [] : [];
  } catch (error) {
    return [];
  }
};

// GET /v1/stores/:code/analytics on the JFL API -> data ({ storeCode,
// storeName, telemetry, controlLogs, ... }), or {}. Dates are YYYY-MM-DD and
// both ends are inclusive. Goes through jflApi so a 401 refreshes the session
// and retries.
export const fetchStoreAnalytics = async (storeCode, startDate, endDate) => {
  try {
    const res = await jflApi.get(
      `/v1/stores/${encodeURIComponent(storeCode)}/analytics`,
      { params: { startDate, endDate } }
    );
    return res.data?.success ? res.data?.data ?? {} : {};
  } catch (error) {
    return {};
  }
};
